/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { Dimension } from '#utils/UNSAFE_visTypes/common';

export function getResolvedDragMode(
  isUserDragModeButtonShown: boolean,
  isZoomAndScroll: boolean,
  isMobile: boolean,
  dragMode: 'off' | 'pan' | 'zoom' | 'user' | 'select',
  userSelectedDragMode?: 'pan' | 'zoom' | 'select',
  isMultiSelection?: boolean
) {
  if (userSelectedDragMode != undefined && isUserDragModeButtonShown) {
    // when dragMode api value change happens, userDragMode state can be stale.
    // only select userDragMode if there is a button that is present to select it.
    return userSelectedDragMode;
  }

  if (dragMode === 'user') {
    if (!isMobile) {
      return isZoomAndScroll ? 'pan' : isMultiSelection ? 'select' : 'off';
    }
  }

  return isMobile ? 'off' : dragMode;
}

export function getMarqueeDims(
  initialPoint: { x: number; y: number } | undefined,
  finalPoint: { x: number; y: number } | undefined,
  plotAreaSpace: Dimension,
  xSpace: Dimension,
  ySpace: Dimension,
  treatAsXSpace: boolean
) {
  if (initialPoint && finalPoint) {
    const spaceWidth = plotAreaSpace.x + plotAreaSpace.width;
    const spaceHeight = plotAreaSpace.y + plotAreaSpace.height;

    let x1 = Math.max(Math.min(initialPoint.x, finalPoint.x), plotAreaSpace.x);
    let x2 = Math.min(Math.max(initialPoint.x, finalPoint.x), spaceWidth);

    let y1 = Math.max(Math.min(initialPoint.y, finalPoint.y), plotAreaSpace.y);
    const y2 = Math.min(Math.max(initialPoint.y, finalPoint.y), spaceHeight);

    let width = Math.abs(x2 - x1);
    let height = Math.abs(y2 - y1);

    if (isPointInsideSpace(initialPoint.x, initialPoint.y, xSpace) || treatAsXSpace) {
      y1 = plotAreaSpace.y;
      height = plotAreaSpace.height;
    } else if (isPointInsideSpace(initialPoint.x, initialPoint.y, ySpace)) {
      x1 = plotAreaSpace.x;
      x2 = spaceWidth;
      width = plotAreaSpace.width;
    }
    return {
      x1,
      y1,
      width,
      height,
      x2,
      y2
    };
  }
  return;
}

export function getCursorType(
  currentDragMode: 'zoom' | 'select' | 'pan' | 'off' | 'user',
  isPointerDown: boolean
) {
  if (currentDragMode === 'zoom' || currentDragMode === 'select') {
    return 'crosshair';
  }

  if (currentDragMode === 'pan') {
    return isPointerDown ? 'grabbing' : 'grab';
  }

  return;
}

export function isPointInsideSpace(x: number, y: number, space: Dimension) {
  if (x >= space.x && x <= space.x + space.width && y >= space.y && y <= space.y + space.height) {
    return true;
  }
  return false;
}

export function isPointOutsideTargetSpace(
  x: number,
  y: number,
  plotAreaSpace: Dimension,
  xSpace: Dimension,
  ySpace: Dimension
) {
  if (
    !isPointInsideSpace(x, y, plotAreaSpace) &&
    !isPointInsideSpace(x, y, xSpace) &&
    !isPointInsideSpace(x, y, ySpace)
  ) {
    return true;
  }
  return false;
}

export function getPanButtonVisibility(
  isZoomAndScroll: boolean,
  dragMode: 'off' | 'pan' | 'zoom' | 'user' | 'select',
  isMobile: boolean
) {
  if (!isMobile || !isZoomAndScroll) {
    return false;
  }

  return dragMode === 'user' || dragMode === 'pan';
}

export function getZoomButtonVisibility(
  isZoomAndScroll: boolean,
  isMobile: boolean,
  dragMode: 'zoom' | 'select' | 'pan' | 'off' | 'user'
) {
  if (!isZoomAndScroll) {
    return false;
  }

  // In mobile don't show for user mode
  return (isMobile && dragMode === 'zoom') || (!isMobile && dragMode === 'user');
}

export function getSelectButtonVisibility(
  isMultiSelection: boolean,
  isZoomScroll: boolean,
  isMobile: boolean,
  dragMode: 'zoom' | 'select' | 'pan' | 'user' | 'off'
) {

  if (!isMultiSelection) {
    return false;
  }
  else if(!isZoomScroll && ( dragMode === 'user' || dragMode === 'select')){
    return isMobile;
  }
  
  return dragMode === 'user' || (isMobile && dragMode === 'select');
}
